import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { navigate, useLocation, useToast, useTRPC } from '@barev/web';
import { useEffect, useMemo, useState } from 'react';
import type { Me } from '../App.tsx';
import { errText, useT } from '../i18n.tsx';
import { Dialog, Field, I, Skeleton } from '../components/kit.tsx';
import { MapView, STAGE_COLORS, STAGE_COLOR_KEYS, type MapPoint } from '../components/map.tsx';

type Tab = 'board' | 'tasks' | 'map';
type Kind = 'VENUE' | 'AGENCY';
export const VENUE_CATEGORIES = ['cafe', 'restaurant', 'shop', 'beauty', 'other'] as const;
const DAY = 86_400_000;
const monthKey = (d = new Date()) => new Date(d.getTime() + 4 * 3600_000).toISOString().slice(0, 7);

export function Crm({ me }: { me: Me }) {
  const { t } = useT();
  const trpc = useTRPC();
  const { query } = useLocation();
  const tab = (query.get('tab') as Tab) || 'board';
  const isSuper = me.roles.includes('SUPERADMIN');
  const [kind, setKind] = useState<Kind | undefined>();
  const [ownerId, setOwnerId] = useState<string | undefined>();
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [dlg, setDlg] = useState<'new' | 'stages' | 'bonus' | null>(null);
  useEffect(() => {
    const id = setTimeout(() => setQ(search.trim()), 300);
    return () => clearTimeout(id);
  }, [search]);
  const team = useQuery(trpc.bo.crm.team.queryOptions());
  const summary = useQuery({ ...trpc.bo.crm.summary.queryOptions({ ownerId }), refetchInterval: 60_000 });

  return (
    <>
      <div className="bo-head">
        <div>
          <h1 className="bo-title">{t('crm.title')}</h1>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <BonusChip me={me} salesId={ownerId} onEdit={isSuper ? () => setDlg('bonus') : undefined} configured={summary.data?.bonusConfigured} />
          {isSuper && (
            <button type="button" className="bo-btn bo-btn--ghost" onClick={() => setDlg('stages')}>
              {t('crm.stagesBtn')}
            </button>
          )}
          <button type="button" className="bo-btn bo-btn--accent" onClick={() => setDlg('new')}>
            <I name="plus" size={16} />
            {t('crm.newLead')}
          </button>
        </div>
      </div>

      <div className="bo-ogroups" role="tablist">
        {(['board', 'tasks', 'map'] as const).map((x) => (
          <button key={x} type="button" role="tab" className="bo-ogroup" aria-selected={tab === x} onClick={() => navigate(x === 'board' ? '/crm' : `/crm?tab=${x}`, { replace: true })}>
            {t(`crm.tab.${x}`)}
            {x === 'tasks' && !!summary.data?.overdue && <span className="bo-chat__dot">{summary.data.overdue}</span>}
          </button>
        ))}
      </div>

      {tab === 'board' && (
        <div className="bo-toolbar">
          <label className="bo-search">
            <I name="search" size={18} />
            <input className="bo-input" type="search" value={search} placeholder={t('crm.search')} aria-label={t('crm.search')} onChange={(e) => setSearch(e.target.value)} />
          </label>
          <div className="bo-pills" role="group">
            {([undefined, 'VENUE', 'AGENCY'] as const).map((k) => (
              <button key={k ?? 'all'} type="button" className="bo-pill" aria-pressed={kind === k} onClick={() => setKind(k)}>
                {k ? t(`crm.kind.${k}`) : t('crm.kind.all')}
              </button>
            ))}
          </div>
          {isSuper && (team.data?.length ?? 0) > 1 && (
            <select className="bo-select" style={{ width: 'auto', minHeight: 36 }} value={ownerId ?? ''} onChange={(e) => setOwnerId(e.target.value || undefined)} aria-label={t('crm.owner')}>
              <option value="">{t('crm.allSales')}</option>
              {team.data!.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
              <option value="none">{t('crm.noOwner')}</option>
            </select>
          )}
        </div>
      )}

      {tab === 'board' && <Board kind={kind} ownerId={ownerId} search={q} showOwner={isSuper} counts={summary.data?.byStage} />}
      {tab === 'tasks' && <Tasks me={me} />}
      {tab === 'map' && <CrmMap />}

      {dlg === 'new' && <LeadDialog me={me} onClose={() => setDlg(null)} />}
      {dlg === 'stages' && <StagesDialog onClose={() => setDlg(null)} />}
      {dlg === 'bonus' && <BonusDialog onClose={() => setDlg(null)} />}
    </>
  );
}

// ───── воронка ─────

function Board({ kind, ownerId, search, showOwner, counts }: { kind?: Kind; ownerId?: string; search: string; showOwner: boolean; counts?: Record<string, number> }) {
  const { t, dateTime } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const stages = useQuery(trpc.bo.crm.stages.queryOptions());
  const board = useQuery({ ...trpc.bo.crm.board.queryOptions({ kind, ownerId, search: search || undefined }), placeholderData: keepPreviousData });
  const [drag, setDrag] = useState<string | null>(null);
  const [over, setOver] = useState<string | null>(null);
  const [pending, setPending] = useState<{ leadId: string; stage: { id: string; kind: string; name: string } } | null>(null);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: trpc.bo.crm.board.queryKey() });
    qc.invalidateQueries({ queryKey: trpc.bo.crm.summary.queryKey() });
  };
  const move = useMutation(trpc.bo.crm.move.mutationOptions({ onSuccess: refresh, onError: (e) => toast(errText(t, e), 'error') }));

  if (!stages.data || !board.data)
    return (
      <div className="bo-crm">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} h={300} />
        ))}
      </div>
    );

  const drop = (stageId: string) => {
    const lead = board.data.find((l) => l.id === drag);
    const stage = stages.data.find((s) => s.id === stageId);
    setDrag(null);
    setOver(null);
    if (!lead || !stage || lead.stageId === stageId) return;
    // «Подключён» и «Отказ» требуют подробностей — открываем диалог
    if (stage.kind === 'WON' && !lead.converted) return setPending({ leadId: lead.id, stage });
    if (stage.kind === 'LOST') return setPending({ leadId: lead.id, stage });
    move.mutate({ id: lead.id, stageId });
  };

  return (
    <>
      <div className="bo-crm">
        {stages.data.map((s) => {
          const leads = board.data.filter((l) => l.stageId === s.id);
          return (
            <section
              key={s.id}
              className="bo-crm__col"
              data-over={over === s.id || undefined}
              style={{ ['--stage' as string]: STAGE_COLORS[s.color] ?? STAGE_COLORS.blue }}
              onDragOver={(e) => {
                if (!drag) return;
                e.preventDefault();
                setOver(s.id);
              }}
              onDragLeave={() => setOver((o) => (o === s.id ? null : o))}
              onDrop={() => drop(s.id)}
            >
              <header className="bo-crm__head">
                <b>{s.name}</b>
                <span>{counts?.[s.id] ?? leads.length}</span>
              </header>
              <div className="bo-crm__cards">
                {leads.map((l) => {
                  const days = Math.floor((Date.now() - l.stageChangedAt.getTime()) / DAY);
                  return (
                    <a
                      key={l.id}
                      href={`#/crm/${l.id}`}
                      className="bo-lead"
                      draggable
                      onDragStart={(e) => {
                        setDrag(l.id);
                        e.dataTransfer.effectAllowed = 'move';
                      }}
                      onDragEnd={() => setDrag(null)}
                      data-dragging={drag === l.id || undefined}
                    >
                      <span className="bo-lead__top">
                        <span className={`bo-chip bo-chip--${l.kind === 'AGENCY' ? 'SUPPORT' : 'provider'}`}>{t(`crm.kind1.${l.kind}`)}</span>
                        {l.converted && <span className="bo-chip bo-chip--ok">{t('crm.connected')}</span>}
                        <span className="bo-grow" />
                        <span className="bo-faint bo-small" title={t('crm.inStage')}>
                          {t('crm.days', { n: days })}
                        </span>
                      </span>
                      <b className="bo-lead__name">{l.name}</b>
                      {(l.contactName || l.phone) && <span className="bo-small bo-muted">{[l.contactName, l.phone].filter(Boolean).join(' · ')}</span>}
                      {l.nextTask && (
                        <span className={`bo-lead__task${l.nextTask.overdue ? ' is-overdue' : ''}`}>
                          <I name="check" size={13} />
                          {l.nextTask.title} · {dateTime(l.nextTask.dueAt)}
                        </span>
                      )}
                      {showOwner && <span className="bo-small bo-faint">{l.owner?.name ?? t('crm.noOwner')}</span>}
                    </a>
                  );
                })}
                {leads.length === 0 && <p className="bo-crm__empty">{t('crm.dropHere')}</p>}
              </div>
            </section>
          );
        })}
      </div>
      <p className="bo-hint" style={{ marginTop: 10 }}>
        {t('crm.dragHint')}
      </p>
      {pending && pending.stage.kind === 'WON' && <ConvertDialog leadId={pending.leadId} canOpen={showOwner} onClose={() => setPending(null)} onDone={refresh} />}
      {pending && pending.stage.kind === 'LOST' && <LostDialog leadId={pending.leadId} stageId={pending.stage.id} onClose={() => setPending(null)} onDone={refresh} />}
    </>
  );
}

// ───── задачи ─────

function Tasks({ me }: { me: Me }) {
  const { t, dateTime } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const [done, setDone] = useState(false);
  const tasks = useQuery(trpc.bo.crm.tasks.queryOptions({ done }));
  const [adding, setAdding] = useState(false);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: trpc.bo.crm.tasks.queryKey() });
    qc.invalidateQueries({ queryKey: trpc.bo.crm.summary.queryKey() });
    qc.invalidateQueries({ queryKey: trpc.bo.crm.board.queryKey() });
  };
  const toggle = useMutation(trpc.bo.crm.setTaskDone.mutationOptions({ onSuccess: refresh, onError: (e) => toast(errText(t, e), 'error') }));
  const isSuper = me.roles.includes('SUPERADMIN');
  const now = Date.now();
  return (
    <section className="bo-panel">
      <div className="bo-panel__head">
        <div className="bo-pills">
          <button type="button" className="bo-pill" aria-pressed={!done} onClick={() => setDone(false)}>
            {t('crm.tasksOpen')}
          </button>
          <button type="button" className="bo-pill" aria-pressed={done} onClick={() => setDone(true)}>
            {t('crm.tasksDone')}
          </button>
        </div>
        <button type="button" className="bo-btn bo-btn--sm" onClick={() => setAdding(true)}>
          <I name="plus" size={16} />
          {t('crm.addTask')}
        </button>
      </div>
      {!tasks.data ? (
        <Skeleton h={160} />
      ) : tasks.data.length === 0 ? (
        <p className="bo-empty">{done ? t('crm.noDoneTasks') : t('crm.noTasks')}</p>
      ) : (
        <ul className="bo-tasks">
          {tasks.data.map((x) => {
            const overdue = !x.doneAt && x.dueAt.getTime() < now;
            return (
              <li key={x.id} data-overdue={overdue || undefined} data-done={!!x.doneAt || undefined}>
                <label className="bo-tasks__check">
                  <input type="checkbox" checked={!!x.doneAt} onChange={(e) => toggle.mutate({ id: x.id, done: e.target.checked })} aria-label={x.title} />
                </label>
                <span className="bo-grow" style={{ minWidth: 0 }}>
                  <b>{x.title}</b>
                  <span className="bo-small bo-muted" style={{ display: 'block' }}>
                    {x.lead ? (
                      <a href={`#/crm/${x.lead.id}`}>{x.lead.name}</a>
                    ) : (
                      t('crm.noLead')
                    )}
                    {isSuper && ` · ${x.assignee.name}`}
                  </span>
                </span>
                <span className="bo-tasks__due">{overdue ? `${t('crm.overdue')} · ` : ''}{dateTime(x.dueAt)}</span>
                {x.lead?.phone && (
                  <a className="bo-iconbtn" href={`tel:${x.lead.phone.replace(/[^\d+]/g, '')}`} aria-label={t('ord.call')} title={t('ord.call')}>
                    <I name="phone" size={18} />
                  </a>
                )}
              </li>
            );
          })}
        </ul>
      )}
      <p className="bo-hint" style={{ marginTop: 12 }}>
        {t('crm.tasksHint')}
      </p>
      {adding && <TaskDialog onClose={() => setAdding(false)} onDone={refresh} />}
    </section>
  );
}

export function TaskDialog({ leadId, onClose, onDone }: { leadId?: string; onClose: () => void; onDone: () => void }) {
  const { t } = useT();
  const trpc = useTRPC();
  const toast = useToast();
  const tomorrow = new Date(Date.now() + DAY + 4 * 3600_000).toISOString().slice(0, 10);
  const [title, setTitle] = useState('');
  const [day, setDay] = useState(tomorrow);
  const [time, setTime] = useState('11:00');
  const add = useMutation(
    trpc.bo.crm.addTask.mutationOptions({
      onSuccess: () => {
        onDone();
        onClose();
      },
      onError: (e) => toast(errText(t, e), 'error'),
    }),
  );
  const presets = [t('crm.tp.call'), t('crm.tp.meet'), t('crm.tp.send'), t('crm.tp.check')];
  return (
    <Dialog
      open
      onClose={onClose}
      title={t('crm.addTask')}
      footer={
        <>
          <button type="button" className="bo-btn bo-btn--ghost" onClick={onClose}>
            {t('ui.close')}
          </button>
          <button type="button" className="bo-btn" disabled={!title.trim() || add.isPending} onClick={() => add.mutate({ leadId, title, dueAt: new Date(`${day}T${time}:00+04:00`) })}>
            {t('ui.save')}
          </button>
        </>
      }
    >
      <Field label={t('crm.taskTitle')}>
        <input className="bo-input" autoFocus value={title} maxLength={200} onChange={(e) => setTitle(e.target.value)} />
      </Field>
      <div className="bo-pills">
        {presets.map((p) => (
          <button key={p} type="button" className="bo-pill" onClick={() => setTitle(p)}>
            {p}
          </button>
        ))}
      </div>
      <div className="bo-row2">
        <Field label={t('crm.taskDay')}>
          <input className="bo-input" type="date" value={day} onChange={(e) => setDay(e.target.value)} />
        </Field>
        <Field label={t('crm.taskTime')}>
          <input className="bo-input" type="time" value={time} step={900} onChange={(e) => setTime(e.target.value)} />
        </Field>
      </div>
      <p className="bo-hint">{t('crm.taskHint')}</p>
    </Dialog>
  );
}

// ───── карта ─────

function CrmMap() {
  const { t } = useT();
  const trpc = useTRPC();
  const data = useQuery(trpc.bo.crm.map.queryOptions());
  const points = useMemo<MapPoint[]>(
    () => [
      ...(data.data?.leads ?? []).map((l) => ({ id: `l:${l.id}`, lat: l.lat, lng: l.lng, title: l.name, sub: l.stage, color: STAGE_COLORS[l.color] ?? STAGE_COLORS.blue!, dim: l.lost })),
      ...(data.data?.partners ?? []).map((p) => ({ id: `p:${p.id}`, lat: p.lat, lng: p.lng, title: p.name, sub: t('crm.partnerOnMap'), color: '#b8235a', strong: true, dim: !p.active })),
    ],
    [data.data, t],
  );
  return (
    <section className="bo-panel" style={{ padding: 10 }}>
      <MapView points={points} height={560} onPick={(id) => navigate(id.startsWith('l:') ? `/crm/${id.slice(2)}` : `/venues/${id.slice(2)}`)} />
      <div className="bo-maplegend">
        <span>
          <i style={{ background: '#b8235a', borderColor: '#4a1028' }} /> {t('crm.legendPartner')}
        </span>
        <span>
          <i style={{ background: STAGE_COLORS.blue }} /> {t('crm.legendLead')}
        </span>
        <span className="bo-faint">{t('crm.mapHint')}</span>
      </div>
    </section>
  );
}

// ───── диалоги ─────

export function LeadDialog({ me, lead, onClose }: { me: Me; lead?: { id: string; kind: Kind; name: string; category: string | null; contactName: string | null; phone: string | null; telegram: string | null; address: string | null; note: string | null; owner: { id: string } | null }; onClose: () => void }) {
  const { t } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const team = useQuery(trpc.bo.crm.team.queryOptions());
  const isSuper = me.roles.includes('SUPERADMIN');
  const [f, setF] = useState({
    kind: (lead?.kind ?? 'VENUE') as Kind,
    name: lead?.name ?? '',
    category: lead?.category ?? 'cafe',
    contactName: lead?.contactName ?? '',
    phone: lead?.phone ?? '',
    telegram: lead?.telegram ?? '',
    address: lead?.address ?? '',
    note: lead?.note ?? '',
    ownerId: lead?.owner?.id ?? me.id,
  });
  const done = (r?: { id: string } | void) => {
    qc.invalidateQueries({ queryKey: trpc.bo.crm.board.queryKey() });
    qc.invalidateQueries({ queryKey: trpc.bo.crm.get.queryKey() });
    qc.invalidateQueries({ queryKey: trpc.bo.crm.summary.queryKey() });
    onClose();
    if (r && 'id' in r) navigate(`/crm/${r.id}`);
  };
  const onError = (e: unknown) => toast(errText(t, e), 'error');
  const create = useMutation(trpc.bo.crm.create.mutationOptions({ onSuccess: done, onError }));
  const update = useMutation(trpc.bo.crm.update.mutationOptions({ onSuccess: () => done(), onError }));
  const payload = {
    name: f.name,
    category: f.kind === 'VENUE' ? (f.category as (typeof VENUE_CATEGORIES)[number]) : null,
    contactName: f.contactName || null,
    phone: f.phone || null,
    telegram: f.telegram || null,
    address: f.address || null,
    note: f.note || null,
    ...(isSuper ? { ownerId: f.ownerId || null } : {}),
  };
  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title={lead ? t('crm.editLead') : t('crm.newLead')}
      footer={
        <>
          <button type="button" className="bo-btn bo-btn--ghost" onClick={onClose}>
            {t('ui.close')}
          </button>
          <button type="button" className="bo-btn" disabled={!f.name.trim() || create.isPending || update.isPending} onClick={() => (lead ? update.mutate({ id: lead.id, ...payload }) : create.mutate({ kind: f.kind, ...payload }))}>
            {t('ui.save')}
          </button>
        </>
      }
    >
      {!lead && (
        <div className="bo-chtabs">
          {(['VENUE', 'AGENCY'] as const).map((k) => (
            <button key={k} type="button" aria-pressed={f.kind === k} onClick={() => setF({ ...f, kind: k })}>
              {t(`crm.kind1.${k}`)}
            </button>
          ))}
        </div>
      )}
      <div className="bo-row2">
        <Field label={t('crm.name')}>
          <input className="bo-input" autoFocus value={f.name} maxLength={120} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder={f.kind === 'VENUE' ? t('crm.namePhVenue') : t('crm.namePhAgency')} />
        </Field>
        {f.kind === 'VENUE' ? (
          <Field label={t('crm.category')}>
            <select className="bo-select" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })}>
              {VENUE_CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {t(`venue.cat.${c}`)}
                </option>
              ))}
            </select>
          </Field>
        ) : (
          <span />
        )}
      </div>
      <div className="bo-row2">
        <Field label={t('crm.contactName')}>
          <input className="bo-input" value={f.contactName} maxLength={120} onChange={(e) => setF({ ...f, contactName: e.target.value })} placeholder={t('crm.contactPh')} />
        </Field>
        <Field label={t('chat.phone')}>
          <input className="bo-input" value={f.phone} maxLength={40} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="+374 …" />
        </Field>
      </div>
      <div className="bo-row2">
        <Field label="Telegram">
          <input className="bo-input" value={f.telegram} maxLength={80} onChange={(e) => setF({ ...f, telegram: e.target.value })} placeholder="@username" />
        </Field>
        <Field label={t('crm.address')}>
          <input className="bo-input" value={f.address} maxLength={200} onChange={(e) => setF({ ...f, address: e.target.value })} />
        </Field>
      </div>
      {isSuper && (
        <Field label={t('crm.owner')}>
          <select className="bo-select" value={f.ownerId} onChange={(e) => setF({ ...f, ownerId: e.target.value })}>
            <option value="">{t('crm.noOwner')}</option>
            {team.data?.map((m) => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </select>
        </Field>
      )}
      <Field label={t('crm.note')}>
        <textarea className="bo-textarea" style={{ minHeight: 70 }} value={f.note} maxLength={4000} onChange={(e) => setF({ ...f, note: e.target.value })} />
      </Field>
    </Dialog>
  );
}

/** «Подключён»: создаём партнёра/агентство и показываем ссылку, которую нужно отправить */
export function ConvertDialog({ leadId, canOpen, onClose, onDone }: { leadId: string; canOpen: boolean; onClose: () => void; onDone: () => void }) {
  const { t } = useT();
  const trpc = useTRPC();
  const toast = useToast();
  const lead = useQuery(trpc.bo.crm.get.queryOptions({ id: leadId }));
  const [code, setCode] = useState('');
  const [result, setResult] = useState<{ kind: 'VENUE' | 'AGENCY'; url: string; id: string } | null>(null);
  const convert = useMutation(
    trpc.bo.crm.convert.mutationOptions({
      onSuccess: (r) => {
        onDone();
        setResult({ kind: r.kind, url: r.url, id: r.kind === 'VENUE' ? r.partnerId : r.agencyId });
      },
      onError: (e) => toast(errText(t, e), 'error'),
    }),
  );
  const isAgency = lead.data?.kind === 'AGENCY';
  useEffect(() => {
    if (lead.data && !code) setCode(lead.data.name.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 12));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lead.data]);
  return (
    <Dialog
      open
      onClose={onClose}
      title={result ? t('crm.convertedTitle') : t('crm.convertTitle', { name: lead.data?.name ?? '' })}
      footer={
        result ? (
          <>
            {canOpen && (
              <a className="bo-btn bo-btn--ghost" href={result.kind === 'VENUE' ? `#/venues/${result.id}` : `#/agencies/${result.id}`} onClick={onClose}>
                {result.kind === 'VENUE' ? t('crm.openVenue') : t('crm.openAgency')}
              </a>
            )}
            <button type="button" className="bo-btn" onClick={onClose}>
              {t('ui.close')}
            </button>
          </>
        ) : (
          <>
            <button type="button" className="bo-btn bo-btn--ghost" onClick={onClose}>
              {t('ui.close')}
            </button>
            <button type="button" className="bo-btn bo-btn--accent" disabled={!lead.data || (isAgency && !code.trim()) || convert.isPending} onClick={() => convert.mutate({ id: leadId, agencyCode: isAgency ? code : undefined })}>
              {t('crm.convert')}
            </button>
          </>
        )
      }
    >
      {result ? (
        <>
          <p style={{ margin: 0 }}>{result.kind === 'VENUE' ? t('crm.convertedVenue') : t('crm.convertedAgency')}</p>
          <div className="bo-linkbox">
            <code>{result.url}</code>
            <button
              type="button"
              className="bo-btn bo-btn--sm"
              onClick={() => {
                void navigator.clipboard?.writeText(result.url);
                toast(t('ui.copied'));
              }}
            >
              {t('ui.copy')}
            </button>
          </div>
        </>
      ) : isAgency ? (
        <>
          <p className="bo-hint">{t('crm.convertAgencyHint')}</p>
          <Field label={t('crm.agencyCode')} hint={t('crm.agencyCodeHint')}>
            <input className="bo-input" value={code} maxLength={32} onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''))} />
          </Field>
        </>
      ) : (
        <p className="bo-hint">{t('crm.convertVenueHint')}</p>
      )}
    </Dialog>
  );
}

export function LostDialog({ leadId, stageId, onClose, onDone }: { leadId: string; stageId: string; onClose: () => void; onDone: () => void }) {
  const { t } = useT();
  const trpc = useTRPC();
  const toast = useToast();
  const [reason, setReason] = useState('');
  const move = useMutation(
    trpc.bo.crm.move.mutationOptions({
      onSuccess: () => {
        onDone();
        onClose();
      },
      onError: (e) => toast(errText(t, e), 'error'),
    }),
  );
  const presets = [t('crm.lr.price'), t('crm.lr.notNow'), t('crm.lr.noAnswer'), t('crm.lr.competitor')];
  return (
    <Dialog
      open
      onClose={onClose}
      title={t('crm.lostTitle')}
      footer={
        <>
          <button type="button" className="bo-btn bo-btn--ghost" onClick={onClose}>
            {t('ui.close')}
          </button>
          <button type="button" className="bo-btn" disabled={!reason.trim() || move.isPending} onClick={() => move.mutate({ id: leadId, stageId, lostReason: reason })}>
            {t('ui.save')}
          </button>
        </>
      }
    >
      <Field label={t('crm.lostReason')}>
        <textarea className="bo-textarea" style={{ minHeight: 70 }} value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
      </Field>
      <div className="bo-pills">
        {presets.map((p) => (
          <button key={p} type="button" className="bo-pill" onClick={() => setReason(p)}>
            {p}
          </button>
        ))}
      </div>
    </Dialog>
  );
}

function StagesDialog({ onClose }: { onClose: () => void }) {
  const { t } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const stages = useQuery(trpc.bo.crm.stages.queryOptions());
  const [list, setList] = useState<{ id?: string; name: string; kind: 'OPEN' | 'WON' | 'LOST'; color: string }[] | null>(null);
  useEffect(() => {
    if (stages.data && !list) setList(stages.data.map((s) => ({ id: s.id, name: s.name, kind: s.kind, color: s.color })));
  }, [stages.data, list]);
  const save = useMutation(
    trpc.bo.crm.saveStages.mutationOptions({
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: trpc.bo.crm.stages.queryKey() });
        toast(t('prv.saved'));
        onClose();
      },
      onError: (e) => toast(errText(t, e), 'error'),
    }),
  );
  const swap = (i: number, j: number) => {
    if (!list || j < 0 || j >= list.length) return;
    const next = [...list];
    [next[i], next[j]] = [next[j]!, next[i]!];
    setList(next);
  };
  return (
    <Dialog
      open
      wide
      onClose={onClose}
      title={t('crm.stagesTitle')}
      footer={
        <>
          <button type="button" className="bo-btn bo-btn--ghost" onClick={() => list && setList([...list, { name: '', kind: 'OPEN', color: 'blue' }])}>
            <I name="plus" size={16} />
            {t('crm.addStage')}
          </button>
          <span className="bo-grow" />
          <button type="button" className="bo-btn bo-btn--ghost" onClick={onClose}>
            {t('ui.close')}
          </button>
          <button type="button" className="bo-btn" disabled={!list || list.some((s) => !s.name.trim()) || save.isPending} onClick={() => list && save.mutate(list)}>
            {t('ui.save')}
          </button>
        </>
      }
    >
      <p className="bo-hint">{t('crm.stagesHint')}</p>
      <ul className="bo-stages">
        {(list ?? []).map((s, i) => (
          <li key={s.id ?? `new-${i}`}>
            <span className="bo-stages__arrows">
              <button type="button" onClick={() => swap(i, i - 1)} aria-label="↑">
                <I name="arrowUp" size={14} />
              </button>
              <button type="button" onClick={() => swap(i, i + 1)} aria-label="↓">
                <I name="arrowDown" size={14} />
              </button>
            </span>
            <input className="bo-input" value={s.name} maxLength={40} onChange={(e) => setList(list!.map((x, k) => (k === i ? { ...x, name: e.target.value } : x)))} />
            <select className="bo-select" value={s.kind} onChange={(e) => setList(list!.map((x, k) => (k === i ? { ...x, kind: e.target.value as 'OPEN' } : x)))}>
              {(['OPEN', 'WON', 'LOST'] as const).map((k) => (
                <option key={k} value={k}>
                  {t(`crm.stageKind.${k}`)}
                </option>
              ))}
            </select>
            <span className="bo-swatches">
              {STAGE_COLOR_KEYS.map((c) => (
                <button key={c} type="button" aria-pressed={s.color === c} style={{ background: STAGE_COLORS[c] }} onClick={() => setList(list!.map((x, k) => (k === i ? { ...x, color: c } : x)))} aria-label={c} />
              ))}
            </span>
            <button type="button" className="bo-iconbtn" onClick={() => setList(list!.filter((_, k) => k !== i))} aria-label={t('chat.tplDelete')}>
              <I name="close" size={16} />
            </button>
          </li>
        ))}
      </ul>
    </Dialog>
  );
}

// ───── бонус ─────

function BonusChip({ me, salesId, onEdit, configured }: { me: Me; salesId?: string; onEdit?: () => void; configured?: boolean }) {
  const { t, amd } = useT();
  const trpc = useTRPC();
  const isSuper = me.roles.includes('SUPERADMIN');
  const who = isSuper ? salesId : undefined;
  const bonus = useQuery({ ...trpc.bo.crm.bonus.queryOptions({ month: monthKey(), salesId: who === 'none' ? undefined : who }) });
  if (!bonus.data) return null;
  const b = bonus.data;
  const title = [t('crm.bonusVenues', { n: b.venues, sum: amd(b.fromVenues) }), t('crm.bonusAgencies', { n: b.agencies, sum: amd(b.fromAgencies) }), t('crm.bonusRevenue', { pct: b.rules.pct, sum: amd(b.fromRevenue) })].join('\n');
  return (
    <button type="button" className="bo-bonus" title={title} onClick={onEdit} disabled={!onEdit}>
      <span className="bo-small">{isSuper && !salesId ? t('crm.bonusMineSuper') : t('crm.bonusMonth')}</span>
      <b className="bo-num">{configured === false ? t('crm.bonusNotSet') : amd(b.total)}</b>
    </button>
  );
}

function BonusDialog({ onClose }: { onClose: () => void }) {
  const { t } = useT();
  const trpc = useTRPC();
  const qc = useQueryClient();
  const toast = useToast();
  const rules = useQuery(trpc.bo.crm.bonusRules.queryOptions());
  const [f, setF] = useState<{ perVenue: string; perAgency: string; pct: string; months: string } | null>(null);
  useEffect(() => {
    if (rules.data && !f) setF({ perVenue: String(rules.data.perVenue), perAgency: String(rules.data.perAgency), pct: String(rules.data.pct), months: String(rules.data.months) });
  }, [rules.data, f]);
  const save = useMutation(
    trpc.bo.crm.setBonusRules.mutationOptions({
      onSuccess: () => {
        qc.invalidateQueries({ queryKey: trpc.bo.crm.bonus.queryKey() });
        qc.invalidateQueries({ queryKey: trpc.bo.crm.summary.queryKey() });
        qc.invalidateQueries({ queryKey: trpc.bo.crm.bonusRules.queryKey() });
        toast(t('prv.saved'));
        onClose();
      },
      onError: (e) => toast(errText(t, e), 'error'),
    }),
  );
  const num = (v: string) => v.replace(/\D/g, '');
  return (
    <Dialog
      open
      onClose={onClose}
      title={t('crm.bonusTitle')}
      footer={
        <>
          <button type="button" className="bo-btn bo-btn--ghost" onClick={onClose}>
            {t('ui.close')}
          </button>
          <button
            type="button"
            className="bo-btn"
            disabled={!f || save.isPending}
            onClick={() => f && save.mutate({ perVenue: Number(f.perVenue) || 0, perAgency: Number(f.perAgency) || 0, pct: Math.min(100, Number(f.pct) || 0), months: Math.max(1, Number(f.months) || 1) })}
          >
            {t('ui.save')}
          </button>
        </>
      }
    >
      {f && (
        <>
          <p className="bo-hint">{t('crm.bonusHint')}</p>
          <div className="bo-row2">
            <Field label={t('crm.bonusPerVenue')}>
              <input className="bo-input" inputMode="numeric" value={f.perVenue} onChange={(e) => setF({ ...f, perVenue: num(e.target.value) })} />
            </Field>
            <Field label={t('crm.bonusPerAgency')}>
              <input className="bo-input" inputMode="numeric" value={f.perAgency} onChange={(e) => setF({ ...f, perAgency: num(e.target.value) })} />
            </Field>
          </div>
          <div className="bo-row2">
            <Field label={t('crm.bonusPct')}>
              <input className="bo-input" inputMode="numeric" value={f.pct} onChange={(e) => setF({ ...f, pct: num(e.target.value) })} />
            </Field>
            <Field label={t('crm.bonusMonths')}>
              <input className="bo-input" inputMode="numeric" value={f.months} onChange={(e) => setF({ ...f, months: num(e.target.value) })} />
            </Field>
          </div>
        </>
      )}
    </Dialog>
  );
}
